// Client-side search over the build-time index in /search-index.json.
// No server: the whole corpus is ~45 short docs, so ranking every one per
// keystroke is cheaper than a network round trip.

export type SearchDoc = {
  slug: string
  title: string
  category: string
  tags: string[]
  summary: string
  headings: string[]
  faq: string[]
  body: string
}

export type SearchHit = { doc: SearchDoc; score: number; matchedTags: string[] }

const STOP = new Set(['the', 'a', 'an', 'of', 'for', 'and', 'or', 'to', 'is', 'in', 'on', 'do', 'does', 'what', 'how'])

export function tokenize(q: string): string[] {
  return q
    .toLowerCase()
    .split(/[^a-z0-9+]+/)
    .filter((t) => t.length > 1 && !STOP.has(t))
}

/** Field weights. Title and tags dominate, body is a tiebreaker. */
const W = { title: 12, tag: 8, heading: 4, faq: 4, summary: 3, category: 3, body: 1 }

function fieldScore(haystack: string, token: string, weight: number): number {
  if (!haystack) return 0
  const i = haystack.indexOf(token)
  if (i === -1) return 0
  // Whole-word or start-of-word matches beat mid-word coincidences.
  const before = i === 0 ? ' ' : haystack[i - 1]
  const boundary = /[^a-z0-9]/.test(before)
  return weight * (boundary ? 1 : 0.35)
}

export function search(docs: SearchDoc[], query: string, limit = 8): SearchHit[] {
  const tokens = tokenize(query)
  if (tokens.length === 0) return []

  const hits: SearchHit[] = []
  for (const doc of docs) {
    const title = doc.title.toLowerCase()
    const tags = doc.tags.map((t) => t.toLowerCase())
    const headings = doc.headings.join(' ').toLowerCase()
    const faq = doc.faq.join(' ').toLowerCase()
    const summary = doc.summary.toLowerCase()
    const body = doc.body.toLowerCase()
    const category = doc.category.toLowerCase()

    let score = 0
    let matchedAll = true
    const matchedTags: string[] = []

    for (const t of tokens) {
      let best = 0
      best = Math.max(best, fieldScore(title, t, W.title))
      for (let i = 0; i < tags.length; i++) {
        const s = fieldScore(tags[i], t, W.tag)
        if (s > 0 && !matchedTags.includes(doc.tags[i])) matchedTags.push(doc.tags[i])
        best = Math.max(best, s)
      }
      best = Math.max(best, fieldScore(headings, t, W.heading))
      best = Math.max(best, fieldScore(faq, t, W.faq))
      best = Math.max(best, fieldScore(summary, t, W.summary))
      best = Math.max(best, fieldScore(category, t, W.category))
      best = Math.max(best, fieldScore(body, t, W.body))
      if (best === 0) matchedAll = false
      score += best
    }

    // Every token must appear somewhere: "coq10 statins" should not return
    // every CoQ10 article. Single-token queries skip the requirement.
    if (score > 0 && (matchedAll || tokens.length === 1)) {
      if (title === query.trim().toLowerCase()) score += 40
      hits.push({ doc, score, matchedTags })
    }
  }

  return hits.sort((a, b) => b.score - a.score || a.doc.title.localeCompare(b.doc.title)).slice(0, limit)
}

let cache: Promise<SearchDoc[]> | null = null

/** Fetched once per session, then reused. */
export function loadIndex(): Promise<SearchDoc[]> {
  if (!cache) {
    cache = fetch('/search-index.json')
      .then((r) => (r.ok ? r.json() : { docs: [] }))
      .then((j: { docs?: SearchDoc[] }) => j.docs ?? [])
      .catch(() => [])
  }
  return cache
}
